'use client';

import { type MouseEventHandler, forwardRef, useCallback, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { useControllableValue } from '../../hooks/use-controllable-value';
import { ChipGroupContext } from '../chip-group/context/chip-group.context';
import { filterChipRootRecipe } from './styles/filter-chip-root.styles';
import type { FilterChipRootPropsInterface } from './types/filter-chip-root.types';

/**
 * @since 10.0.0
 */
export const FilterChipRoot = forwardRef<HTMLButtonElement, FilterChipRootPropsInterface>(
  (props: FilterChipRootPropsInterface, forwardedRef) => {
    const chipGroupContext = useContext(ChipGroupContext);

    const {
      children,
      value,
      elevated = chipGroupContext.elevated || false,
      className,
      css: cssProp,
      disabled = chipGroupContext.disabled || false,
      onClick,
      selected,
      defaultSelected,
      onSelectedChange,
      ...rest
    } = props;

    // Determine if this chip is part of a group selection
    const isInGroup = Boolean(chipGroupContext.handleSelection && value);

    // When in a group, get selection state from group context
    const isSelectedInGroup = isInGroup && chipGroupContext.isSelected?.(value!);

    // Only use individual state management when NOT in a group
    const [individualSelectedState, setIndividualSelectedState] = useControllableValue<boolean>({
      controlledValue: isInGroup ? false : (selected ?? false),
      uncontrolledValue: isInGroup ? false : (defaultSelected ?? false),
      isControlled: !isInGroup && 'selected' in props,
      isUncontrolled: !isInGroup && 'defaultSelected' in props,
      onChange: onSelectedChange,
      componentName: 'FilterChip.Root',
      controlledPropName: 'selected',
      uncontrolledPropName: 'defaultSelected',
    });

    // The final selected state
    const selectedState = isInGroup ? isSelectedInGroup : individualSelectedState;

    const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
      (e) => {
        if (!disabled) {
          onClick?.(e);

          if (isInGroup && value && chipGroupContext.handleSelection) {
            // Use group selection logic
            chipGroupContext.handleSelection(value);
          } else {
            // Use individual chip logic
            setIndividualSelectedState((prev) => !prev);
          }
        }
      },
      [setIndividualSelectedState, disabled, onClick, isInGroup, value, chipGroupContext],
    );

    const styles = css(
      filterChipRootRecipe.raw({
        elevated,
        disabled,
        selected: !!selectedState,
      }),
      cssProp,
    );

    return (
      <button
        ref={forwardedRef}
        className={cx('c11n', 'c11n_filter-chip__root', styles, className)}
        aria-pressed={!!selectedState}
        data-c11n-component="FilterChip.Root"
        data-c11n-version={__C11N_VERSION__}
        disabled={disabled}
        onClick={handleClick}
        type="button"
        {...rest}
      >
        {children}
      </button>
    );
  },
);

FilterChipRoot.displayName = 'FilterChip.Root';
